// src/components/subscription/UpgradePromptModal.tsx

import React, { useEffect, useState } from 'react';
import { Zap, Target, Check, X } from 'lucide-react';
import { AIFeature } from '../../hooks/useAIUsageLimit';

interface UpgradePromptModalProps {
  isOpen: boolean;
  onClose: () => void;
  feature?: AIFeature;
  trigger?: 'usage-limit' | 'trial-end' | 'manual';
}

/**
 * 升級提示 Modal
 * 當用戶達到使用限制或試用期結束時顯示
 */
export const UpgradePromptModal: React.FC<UpgradePromptModalProps> = ({
  isOpen,
  onClose,
  feature,
  trigger = 'usage-limit'
}) => {
  const [isVisible, setIsVisible] = useState(false);
  
  useEffect(() => {
    if (isOpen) {
      setIsVisible(true);
    } else {
      const timer = setTimeout(() => setIsVisible(false), 300);
      return () => clearTimeout(timer);
    }
  }, [isOpen]);
  
  if (!isVisible) return null;
  
  // 根據觸發原因和功能類型定制內容
  const getContent = () => {
    const featureNames: Record<AIFeature, string> = {
      [AIFeature.AI_PLANNING]: 'Smart Daily Planning',
      [AIFeature.CAPTURE_ANALYSIS]: 'Smart Analysis',
      [AIFeature.SMART_SUGGESTIONS]: 'Smart Suggestions',
      [AIFeature.DATE_SUMMARY]: 'Date Summaries',
      [AIFeature.DEFAULT]: 'Smart Features'
    };
    
    const currentFeature = feature ? featureNames[feature] : 'Smart Features';
    
    if (trigger === 'usage-limit') {
      return {
        titleIcon: undefined,
        title: 'Daily Limit Reached',
        subtitle: `You've used all your ${currentFeature} for today`,
        message: "You've experienced the power of unlimited smart features during your trial. Get it back with Pro!",
        ctaPrimary: 'Upgrade to Pro',
        ctaSecondary: 'Maybe Tomorrow'
      };
    }
    
    if (trigger === 'trial-end') {
      return {
        titleIcon: <Target size={22} className="text-gray-700" />,
        title: 'Trial Complete - Thank You!',
        subtitle: 'Your free trial has ended',
        message: "You've experienced the full power of Orlo Pro. Keep the momentum going!",
        ctaPrimary: 'Continue with Pro',
        ctaSecondary: 'Use Free Version'
      };
    }

    // Default manual trigger
    return {
      titleIcon: <Zap size={22} className="text-gray-700" />,
      title: 'Unlock Full Potential',
      subtitle: 'Upgrade to Orlo Pro',
      message: 'Get unlimited smart features and advanced tools to supercharge your productivity.',
      ctaPrimary: 'Start Pro Plan',
      ctaSecondary: 'Not Now'
    };
  };
  
  const content = getContent();
  
  // 功能對比數據（freeAvailable: false 表示免費版不可用）
  const featureComparison = [
    { name: 'Smart Daily Planning', free: '1/day', pro: 'Unlimited', freeAvailable: true },
    { name: 'Smart Analysis', free: '5/day', pro: 'Unlimited', freeAvailable: true },
    { name: 'Smart Suggestions', free: '3/day', pro: 'Unlimited', freeAvailable: true },
    { name: 'Advanced Analytics', free: null, pro: 'Unlimited', freeAvailable: false },
    { name: 'Priority Support', free: null, pro: 'Included', freeAvailable: false },
    { name: 'Early Features', free: null, pro: 'Included', freeAvailable: false }
  ];
  
  const handleUpgrade = () => {
    // TODO: 整合付費流程（Polar.sh 或其他付費服務）
    window.open('/profile/subscription', '_blank');
    onClose();
  };
  
  const handleClose = () => {
    onClose();
  };
  
  return (
    <div className="fixed inset-0 z-modal flex items-center justify-center">
      {/* 背景遮罩 */}
      <div 
        className={`absolute inset-0 bg-black transition-opacity duration-300 ${
          isOpen ? 'opacity-50' : 'opacity-0'
        }`}
        onClick={handleClose}
      />
      
      {/* Modal 內容 */}
      <div 
        className={`relative bg-white rounded-2xl shadow-2xl max-w-md w-full mx-4 transform transition-all duration-300 ${
          isOpen ? 'scale-100 opacity-100' : 'scale-95 opacity-0'
        }`}
      >
        {/* 關閉按鈕 */}
        <button
          onClick={handleClose}
          className="absolute top-4 right-4 w-8 h-8 flex items-center justify-center rounded-full hover:bg-gray-100 transition-colors"
        >
          <span className="text-gray-400 text-xl">×</span>
        </button>
        
        {/* Header */}
        <div className="p-6 text-center border-b border-gray-100">
          <h2 className="text-2xl font-bold text-gray-900 mb-2 flex items-center justify-center gap-2">
            {content.titleIcon}
            {content.title}
          </h2>
          <p className="text-gray-600">
            {content.subtitle}
          </p>
        </div>
        
        {/* Content */}
        <div className="p-6">
          <div className="text-center mb-6">
            <p className="text-gray-700 leading-relaxed">
              {content.message}
            </p>
          </div>
          
          {/* 功能對比表 */}
          <div className="mb-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-3">
              What you get with Pro:
            </h3>
            <div className="space-y-2">
              {featureComparison.map((item, index) => (
                <div key={index} className="flex items-center justify-between py-2 px-3 bg-gray-50 rounded-lg">
                  <span className="text-sm font-medium text-gray-700">
                    {item.name}
                  </span>
                  <div className="flex items-center space-x-3">
                    <span className="text-xs text-gray-500 min-w-[50px] text-right">
                      {item.freeAvailable
                        ? item.free
                        : <X size={14} className="inline text-gray-400" />
                      }
                    </span>
                    <span className="text-xs font-semibold text-sky-600 min-w-[70px] text-right flex items-center justify-end gap-1">
                      {!item.freeAvailable && <Check size={14} className="text-sky-600" />}
                      {item.pro}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>
          
          {/* 價格顯示 */}
          <div className="mb-6 text-center">
            <div className="inline-flex items-center space-x-2 bg-sky-50 px-4 py-2 rounded-lg">
              <span className="text-2xl font-bold text-sky-600">$12</span>
              <span className="text-gray-600">/month</span>
            </div>
            <p className="text-xs text-gray-500 mt-1">
              Cancel anytime • 30-day money back guarantee
            </p>
          </div>
        </div>
        
        {/* Actions */}
        <div className="p-6 border-t border-gray-100 space-y-3">
          <button
            onClick={handleUpgrade}
            className="w-full bg-sky-600 hover:bg-sky-700 text-white font-semibold py-3 px-4 rounded-lg transition-colors focus:outline-none focus:ring-2 focus:ring-sky-500 focus:ring-offset-2"
          >
            {content.ctaPrimary}
          </button>
          <button
            onClick={handleClose}
            className="w-full bg-gray-100 hover:bg-gray-200 text-gray-700 font-medium py-3 px-4 rounded-lg transition-colors focus:outline-none focus:ring-2 focus:ring-gray-300 focus:ring-offset-2"
          >
            {content.ctaSecondary}
          </button>
        </div>
      </div>
    </div>
  );
};

/**
 * 升級提示管理 Hook
 * 處理全局的升級提示邏輯
 */
export const useUpgradePrompt = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [modalProps, setModalProps] = useState<{
    feature?: AIFeature;
    trigger?: 'usage-limit' | 'trial-end' | 'manual';
  }>({});
  
  useEffect(() => {
    // 監聽全局升級事件
    const handleShowUpgrade = (event: CustomEvent) => {
      const { feature, trigger } = event.detail;
      setModalProps({ feature, trigger });
      setIsOpen(true);
    };
    
    window.addEventListener('show-upgrade-modal' as keyof WindowEventMap, handleShowUpgrade as EventListener);

    return () => {
      window.removeEventListener('show-upgrade-modal' as keyof WindowEventMap, handleShowUpgrade as EventListener);
    };
  }, []);
  
  const showUpgradeModal = (feature?: AIFeature, trigger: 'usage-limit' | 'trial-end' | 'manual' = 'manual') => {
    setModalProps({ feature, trigger });
    setIsOpen(true);
  };
  
  const hideUpgradeModal = () => {
    setIsOpen(false);
  };
  
  return {
    isOpen,
    modalProps,
    showUpgradeModal,
    hideUpgradeModal,
    UpgradeModal: () => (
      <UpgradePromptModal
        isOpen={isOpen}
        onClose={hideUpgradeModal}
        {...modalProps}
      />
    )
  };
};

export default UpgradePromptModal;